import React from 'react';
import Section from './ui/Section';
import Segmented from './ui/Segmented';

/*
 * Help, for the person reading it.
 *
 * This was one long page written for whoever ran the club — add players,
 * generate the draw, approve registrations — and a member opening it to
 * find out how to enter a competition read eleven sections about running
 * one first. It had also fallen a year behind: nothing about the New
 * members page, posters, the newsletter, sign-in links or seasons.
 *
 * Now it is a list of topics, each tagged with who it is for. A player sees
 * the player topics; a club admin sees those and the club's; a super-admin
 * sees the site's as well. Every topic opens on its own so the page stays
 * short, and the search box reaches all of them. An admin can flip to
 * "As a member sees it" to know what their members have been told.
 *
 * Kept as data rather than a page of markup so that adding a feature is
 * adding an entry — and the audience is decided once, on the entry, rather
 * than by an `isAdmin &&` somebody forgets to write.
 */

const PLAYER = 'player';
const ADMIN = 'admin';
const SITE = 'site';
/* Club admins too, but its own group: running a competition is a job of its
   own, with more to it than anything else an admin does here. */
const COMPS = 'comps';

const K = ({ children }) => <strong>{children}</strong>;

const TOPICS = [
  /* ---------------------------------------------------------------- players */
  {
    id: 'signing-in', audience: PLAYER, icon: 'sign-in', title: 'Signing in, and forgotten passwords',
    keywords: 'login log in password forgot reset magic link email account sign up register locked paused too many attempts',
    body: (
      <>
        <p>Sign in with your email and password. If you have forgotten it — or never had one — choose <K>Forgot your password?</K> and we will email you a link.</p>
        <ul>
          <li>Open the link and press <K>Sign me in</K>. You do not need your password.</li>
          <li>If you would like a password for next time, choose <K>Choose one</K> on the same page.</li>
          <li>The link works once, for 30 minutes. If it has run out, ask for another.</li>
        </ul>
        <p>After ten wrong passwords, signing in to that account pauses for 15 minutes to stop anyone guessing. Use <K>Forgot your password?</K> instead of trying again. New passwords need at least 8 characters — a short phrase you will remember works well.</p>
        <p>Signing up with an email your club already has on file sends a link to that address instead, so only you can take over your own record.</p>
      </>
    ),
  },
  {
    id: 'home', audience: PLAYER, icon: 'home', title: 'Your home page',
    keywords: 'home dashboard next up entered season ladder coming up',
    body: (
      <>
        <p>Home shows what matters to you, most urgent first:</p>
        <ul>
          <li><K>Next up</K> — your next game.</li>
          <li><K>Open for entry</K> — competitions you can still enter.</li>
          <li><K>You are entered in</K> and <K>Your season</K> — what you are playing and how it is going.</li>
          <li><K>Membership</K> — your type and when it expires. Renew from <K>My ID</K>.</li>
          <li><K>Ladder</K> and <K>Coming up at the club</K>.</li>
        </ul>
        <p>On a phone the bar at the bottom takes you to Home, My ID, Comps, Events and your Profile; <K>More</K> has the rest.</p>
      </>
    ),
  },
  {
    id: 'entering', audience: PLAYER, icon: 'trophy', title: 'Entering a competition',
    keywords: 'enter register competition team pairs triples fours qr code closed membership type',
    body: (
      <>
        <p>Find the competition under <K>Open for entry</K> on Home or under <K>Comps</K>, and press <K>Register for this Competition</K>. If the club put up a QR code, scanning it takes you to the same place.</p>
        <ul>
          <li>For pairs, triples and fours you give your team a name and pick your team-mates.</li>
          <li>Some competitions are only open to certain membership types — it will say so before you try.</li>
          <li>Your entry may need the club to approve it. You will see it as pending until they do.</li>
          <li>Once entries close, the button goes.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'scores', audience: PLAYER, icon: 'pencil-square-o', title: 'Your games and scores',
    keywords: 'score draw fixtures match results submit approve ends',
    body: (
      <>
        <p>Open a competition to see the draw and the results so far. For a game you played in, open the match and enter the score end by end — press Tab or Enter to move down a column.</p>
        <p>Scores you put in are sent to the club to approve, and show on the results once they have.</p>
      </>
    ),
  },
  {
    id: 'member-id', audience: PLAYER, icon: 'id-card', title: 'My ID — your membership card',
    keywords: 'id card qr membership number expires renew',
    body: (
      <p><K>My ID</K> is your membership card: your membership number and a QR code the club can scan, your membership type and status, and when it expires. When it is due, <K>Renew my membership</K> appears; if you never finished applying, <K>Complete my membership application</K> does.</p>
    ),
  },
  {
    id: 'membership', audience: PLAYER, icon: 'address-card', title: 'Applying for and renewing membership',
    keywords: 'membership apply renew form pending approved subs card board financial',
    body: (
      <>
        <p>Fill in the club’s membership form from <K>Renew my membership</K> on My ID, or from the membership link the club shares. You get an email to say it arrived, with a link to sign in.</p>
        <p>After that it goes through the club’s own steps:</p>
        <ul>
          <li>an admin approves the form;</li>
          <li>you pay your subs;</li>
          <li>the board signs you off and issues your card.</li>
        </ul>
        <p>Your profile shows where you are. When your membership expires you show as unfinancial until you renew.</p>
      </>
    ),
  },
  {
    id: 'events', audience: PLAYER, icon: 'calendar', title: 'Events and what’s on',
    keywords: 'events calendar whats on posters club night market',
    body: (
      <p><K>Events</K> lists everything coming up at the club that is not a competition — club nights, working bees, markets — with the club’s poster where there is one. Repeating events show when they next happen.</p>
    ),
  },
  {
    id: 'results', audience: PLAYER, icon: 'list-ol', title: 'Results, the ladder and ratings',
    keywords: 'results ladder leaderboard rating overview season year filter',
    body: (
      <>
        <p><K>Results</K> and <K>Overview</K> (under More) show finished competitions and the club’s season. The <K>Ladder</K> ranks players and teams by rating.</p>
        <p>The season filter at the bottom of the page follows your club’s playing year — at most clubs July to June, shown as “2025–26”.</p>
        <p>Your <K>rating</K> is out of 100 and rewards how well you play, not just how often:</p>
        <div className="w3-responsive">
          <table className="w3-table w3-bordered w3-small help-table">
            <thead><tr><th>Part</th><th>Weight</th><th>What it measures</th></tr></thead>
            <tbody>
              <tr><td>Win rate</td><td>50%</td><td>Games won, adjusted so a few lucky games do not count for too much.</td></tr>
              <tr><td>Ends won</td><td>20%</td><td>How many ends you win — a close loss still shows good bowling.</td></tr>
              <tr><td>Shot margin</td><td>15%</td><td>How much you win or lose by, compared with the club.</td></tr>
              <tr><td>Comp wins</td><td>10%</td><td>Competitions won out of those entered.</td></tr>
              <tr><td>Activity</td><td>5%</td><td>Enough games to be sure of the rest.</td></tr>
            </tbody>
          </table>
        </div>
        <p>With only one or two games you sit near the middle. After five or so, the rating reflects how you actually play.</p>
      </>
    ),
  },
  {
    id: 'profile', audience: PLAYER, icon: 'user', title: 'Your profile and club emails',
    keywords: 'profile emails unsubscribe newsletter preferences career stats',
    body: (
      <>
        <p><K>Profile</K> shows your record: games, wins, competitions and teams. If any of your details are wrong, ask a club admin to change them.</p>
        <p>Under <K>Club emails</K> you choose what the club sends you. Every club email also has a link at the bottom to change this.</p>
        <p>Want a copy of everything GreenSheet holds about you? Ask your club admin, who can download it for you.</p>
      </>
    ),
  },
  {
    /* Spelled out step by step, because "add it to your home screen" is
       exactly the instruction that loses people: the button is hidden in a
       different menu on each phone, and on an iPhone it is not where anybody
       would look for it. */
    id: 'phone', audience: PLAYER, icon: 'mobile', title: 'Putting GreenSheet on your phone like an app',
    keywords: 'phone mobile home screen app install iphone ipad android safari chrome samsung icon add to home screen',
    body: (
      <>
        <p>GreenSheet works in your phone’s web browser, and you can add it to your home screen so it has its own GreenSheet icon. It then opens full screen, without the browser’s address bar, just like an app — and there is nothing to download from an app store.</p>

        <p className="help-subhead"><i className="fa fa-apple" aria-hidden="true"></i> iPhone or iPad</p>
        <ol>
          <li>Open GreenSheet in <K>Safari</K> (the compass icon) and sign in.</li>
          <li>Tap the <K>Share</K> button — a square with an arrow pointing up. It is at the bottom of the screen on an iPhone and at the top on an iPad. On the newest iPhones, tap the <K>•••</K> button first, then <K>Share</K>.</li>
          <li>Scroll down the list and tap <K>Add to Home Screen</K>. If you cannot see it, tap <K>Edit Actions</K> at the bottom of the list and add it from there.</li>
          <li>Leave the name as GreenSheet and tap <K>Add</K>.</li>
        </ol>
        <p>On recent iPhones this works from Chrome too, through its Share button. If <K>Add to Home Screen</K> is missing, use Safari.</p>

        <p className="help-subhead"><i className="fa fa-android" aria-hidden="true"></i> Android</p>
        <ol>
          <li>Open GreenSheet in <K>Chrome</K> and sign in.</li>
          <li>Tap the <K>⋮</K> menu (three dots) at the top right.</li>
          <li>Tap <K>Install app</K> — on some phones it says <K>Add to home screen</K>.</li>
          <li>Tap <K>Install</K> (or <K>Add</K>) to confirm.</li>
        </ol>
        <p>Chrome sometimes offers this by itself, with an <K>Install</K> message at the bottom of the screen. On a Samsung phone using <K>Samsung Internet</K>, tap the <K>≡</K> menu, then <K>Add page to</K>, then <K>Home screen</K>.</p>

        <p className="help-subhead">Once it is on your home screen</p>
        <ul>
          <li>Tap the GreenSheet icon to open it. You stay signed in, so you will not need your password each time.</li>
          <li>It updates by itself — there is nothing to install again.</li>
          <li>To remove it, press and hold the icon and choose <K>Remove</K> (or <K>Delete App</K> on an iPhone). This does not affect your account.</li>
          <li>Tables you can scroll sideways have a fade at the right-hand edge.</li>
        </ul>
      </>
    ),
  },

  /* ------------------------------------------------------------ club admins */
  {
    id: 'admin-start', audience: ADMIN, icon: 'rocket', title: 'Getting a club started',
    keywords: 'quick start setup new club first steps',
    body: (
      <ol>
        <li><K>Club settings</K> (Admin): name, logo and colours, your <K>playing season</K>, membership types and fees, and the membership form.</li>
        <li><K>Players</K>: add members, or let them sign up and apply through the membership form.</li>
        <li><K>Competitions</K>: create one from a preset, take entries, make the draw and enter scores.</li>
        <li>Put events, posters and the club screen to work when you are ready.</li>
      </ol>
    ),
  },
  {
    id: 'admin-members', audience: ADMIN, icon: 'users', title: 'The members list',
    keywords: 'members users role status verified blocked financial membership type merge duplicate edit',
    body: (
      <>
        <p><K>Admin → Members</K> lists everyone at the club. From each row you can change membership type, player type, role, Financial and Status. Click a name to open the full profile.</p>
        <ul>
          <li><K>Edit</K> on a profile changes everything, including the <K>Membership record</K>: status (no form on file / waiting for approval / approved), membership number, and the dates the form came in, was approved, subs were paid, the card was issued and renewal is due. Use it for members who joined before the online form.</li>
          <li>Setting someone to <K>Waiting for approval</K> sends them back through approval and on to New members.</li>
          <li><K>Merge</K> joins two records for the same person: their competitions and teams move to the one you keep.</li>
          <li><K>Status</K>: Verified once their membership is approved; <K>Blocked</K> stops them signing in and lists them under New members.</li>
          <li>You cannot change a super-admin’s account, or hand out super-admin.</li>
          <li>The <K>Record</K> panel on a profile shows the <K>change history</K> — who changed what, and when, including changes GreenSheet makes itself, such as a membership lapsing. <K>Download their information</K> gives you a copy of everything held about a member, for when they ask for it.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'admin-approvals', audience: ADMIN, icon: 'bell', title: 'Approvals',
    keywords: 'approvals pending bell registrations memberships scores approve',
    body: (
      <>
        <p>The bell at the top appears when something is waiting: competition entries, membership forms, and scores players have put in. It takes you to the first of them.</p>
        <p>Membership forms are approved from <K>Members → Pending membership approvals</K>, or with <K>Confirm Membership</K> on the profile. Only a club admin can approve one.</p>
      </>
    ),
  },
  {
    id: 'admin-intake', audience: ADMIN, icon: 'id-badge', title: 'New members: subs and cards',
    keywords: 'new members intake board card issued subs paid filter select all bulk blocked',
    body: (
      <>
        <p><K>Admin → New members</K> lists everyone approved who has not yet paid or been given a card. They leave the list when both are ticked, and come back when they send a renewal.</p>
        <ul>
          <li>The stage buttons narrow the list: <K>Ready for the board</K> (paid, no card yet), <K>Subs owed</K>, and <K>Waiting 14+ days</K>. Filter further by type, player type, or joining against renewing.</li>
          <li>Tick members, or tick the header box to select everyone shown, then <K>Mark card issued</K> or <K>Mark subs paid</K>. Set the date to the board meeting’s.</li>
          <li><K>Blocked</K> accounts are listed at the bottom, with <K>Unblock</K>.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'admin-rules', audience: ADMIN, icon: 'refresh', title: 'What happens automatically',
    keywords: 'financial automatic expire lapse verified approve life member renewal date',
    body: (
      <ul>
        <li><K>Subs paid</K> makes a member Financial; un-ticking it undoes that.</li>
        <li>When a membership passes its <K>renewal date</K> the member becomes unfinancial. If you switch them back on by hand, that sticks until their next renewal date.</li>
        <li><K>Approving</K> a membership makes the account Verified (a blocked account stays blocked).</li>
        <li><K>Life members</K> have no renewal date and never lapse.</li>
      </ul>
    ),
  },
  {
    id: 'admin-players', audience: ADMIN, icon: 'user-plus', title: 'Players and guests',
    keywords: 'players add player guest visitor non-member filter duplicate',
    body: (
      <>
        <p><K>Players</K> lists the club’s players. Filter by <K>Members</K> or <K>Guests</K>.</p>
        <p><K>Add Guest</K> writes down a visitor: no login, and they do not count towards your member limit. You can add one from the Players page, the members list, or straight into a team. If someone of that name already exists you are shown how to tell them apart before adding.</p>
      </>
    ),
  },
  {
    id: 'admin-teams', audience: ADMIN, icon: 'shield', title: 'Club teams',
    keywords: 'club teams roster pairs triples fours active reserve import',
    body: (
      <p><K>Teams</K> holds the club’s standing sides. Create one, choose pairs, triples or fours, and add players — or <K>Add guest</K> to put a visitor straight in. Switch players between active and reserve. Inside a competition you can import a club team rather than building it again.</p>
    ),
  },
  {
    id: 'admin-events', audience: ADMIN, icon: 'calendar-plus-o', title: 'Events, calendar and gallery',
    keywords: 'events calendar gallery photos season sheet',
    body: (
      <ul>
        <li><K>Events</K>: anything on that is not a competition. Members only see published, uncancelled, upcoming ones.</li>
        <li><K>Calendar</K>: everything on a month grid — events and competitions together. Adding here creates the same records.</li>
        <li><K>Gallery</K>: every picture the club has. Posters, the newsletter and the club website all draw from it. Before uploading, tick to confirm everyone in the photos is happy for them to be used — and a parent or guardian has agreed for anyone under 18.</li>
      </ul>
    ),
  },
  {
    id: 'admin-posters', audience: ADMIN, icon: 'picture-o', title: 'Posters',
    keywords: 'posters ai generate image brief cost style',
    body: (
      <p><K>Posters</K> makes a poster for an event or competition. The assistant drafts the brief from what the club already knows and asks a question or two first, because each picture costs money — what it will cost, and what is left this month, is on the button. Dates and prices are printed from your records, never invented. Posters you keep teach it your club’s usual look.</p>
    ),
  },
  {
    id: 'admin-newsletter', audience: ADMIN, icon: 'newspaper-o', title: 'Newsletter and mailing list',
    keywords: 'newsletter mailing list send test subscribers notes ai',
    body: (
      <>
        <p><K>Newsletter</K> writes the month’s letter from your diary — events, competitions open for entry, results and the ladder — plus your own notes, which lead. <K>Check my notes</K> points out anything a member would need to act on. Edit the draft, send yourself a test, then send.</p>
        <p>For safety, links in a generated letter only ever go to GreenSheet or your club’s website. <K>Mailing list</K> is who it goes to — members or not. Only a person can re-subscribe themselves.</p>
      </>
    ),
  },
  {
    id: 'admin-emails', audience: ADMIN, icon: 'envelope', title: 'Emails and templates',
    keywords: 'email templates placeholders schedule renewal reminders welcome confirmation loginurl',
    body: (
      <>
        <p><K>Club settings → Email templates</K> holds the emails GreenSheet sends for you — welcome, entry received, membership received, renewal due. Placeholders such as <code>{'{{userName}}'}</code> fill in each person’s details; each template lists the ones it accepts.</p>
        <p>The membership confirmation includes a sign-in button. To put it somewhere particular, place <code>{'{{loginUrl}}'}</code> in your template. Renewal reminders go when you press the button, never on their own.</p>
      </>
    ),
  },
  {
    id: 'admin-screen', audience: ADMIN, icon: 'desktop', title: 'The club screen',
    keywords: 'club screen tv display noticeboard scores ladder',
    body: (
      <p><K>Club screen</K> drives the TV in the clubhouse: live scores, the ladder, notices and posters. The preview is exactly what the TV shows, so you can check it without walking into the other room.</p>
    ),
  },
  {
    id: 'admin-settings', audience: ADMIN, icon: 'cog', title: 'Club settings',
    keywords: 'settings season playing year theme logo membership form fees renewal match rules website plan',
    body: (
      <ul>
        <li><K>General</K>: details, greens and rinks, and the <K>playing season</K> — the month your season starts. The season filter, ladder and analytics follow it.</li>
        <li><K>Theme &amp; logos</K>: the club’s colours and logos.</li>
        <li><K>Membership</K>: types and fees, the renewal date, and the membership form.</li>
        <li><K>Match settings</K> and <K>Rules bank</K>: defaults for new competitions.</li>
        <li><K>Club website</K>: the club’s own public page. Under <K>Your own web address</K> you can put it on the club’s existing domain (such as www.yourclub.co.nz) — the steps for whoever manages your domain are there, and <K>Check</K> tells you when it is working.</li>
        <li><K>Plan</K>: what the club is on and how much of it is used.</li>
      </ul>
    ),
  },

  /* ------------------------------------------------------- competitions */
  {
    id: 'comp-overview', audience: COMPS, icon: 'road', title: 'A competition from start to finish',
    keywords: 'competition overview steps tabs order workflow home players teams draw scores results analytics',
    body: (
      <>
        <p>Every competition has the same tabs along the top, and you work through them left to right:</p>
        <ol>
          <li><K>Home</K> — the summary members see: format, dates, entry fee, who can enter, the rules, upcoming and played games, and the leaderboard.</li>
          <li><K>Players</K> — who is entered.</li>
          <li><K>Teams</K> — who plays with whom (skipped for singles).</li>
          <li><K>Draw</K> — who plays whom, when, and on which rink.</li>
          <li><K>Scores</K> — results as they come in.</li>
          <li><K>Results</K> — the standings, then finishing the competition.</li>
        </ol>
        <p>Change any setting later with the <K>gear</K> icon on the competition. A few things lock as you go, so a finished draw cannot be changed by accident — see <K>Finishing, and fixing mistakes</K>.</p>
      </>
    ),
  },
  {
    id: 'comp-create', audience: COMPS, icon: 'plus-circle', title: 'Setting up a competition',
    keywords: 'new competition create preset template championship twilight jackpot knockout sets 2-4-2 social calendar',
    body: (
      <>
        <p>Choose <K>New Competition</K> (or click a day on the <K>Calendar</K> and choose New competition — the date is filled in for you). Pick a preset, check the settings, give it a name and dates under <K>The basics</K>, and press <K>Create Competition</K>.</p>
        <div className="w3-responsive">
          <table className="w3-table w3-bordered w3-small help-table">
            <thead><tr><th>Preset</th><th>What you get</th></tr></thead>
            <tbody>
              <tr><td>Championship Singles</td><td>First to 21, 4 bowls, round robin</td></tr>
              <tr><td>Championship Pairs / Triples / Fours</td><td>18 ends, round robin</td></tr>
              <tr><td>Twilight / Social Bowls</td><td>Triples, 2 sets of 5 ends, twilight scoring, an extra end if the sets are tied</td></tr>
              <tr><td>2-4-2 Pairs</td><td>10 ends</td></tr>
              <tr><td>Social/Jackpot Pairs</td><td>75 minutes, timed</td></tr>
              <tr><td>Random Draw Teams</td><td>Pairs drawn at random on the day, 35 minutes</td></tr>
              <tr><td>Social Triples</td><td>10 ends</td></tr>
              <tr><td>First to 21 Singles</td><td>Play to a target score</td></tr>
              <tr><td>Sets Play Pairs</td><td>Best of 3 sets of 9 ends</td></tr>
              <tr><td>Knockout Singles / Pairs</td><td>Lose once and you are out</td></tr>
            </tbody>
          </table>
        </div>
        <ul>
          <li>Changed the settings into something you will run again? <K>Save as Template</K> adds it to the list of presets for your club.</li>
          <li><K>Restore site defaults</K> puts the settings back how they started.</li>
          <li>A super-admin must pick a club first — a competition belongs to one club.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'comp-settings', audience: COMPS, icon: 'sliders', title: 'Competition settings explained',
    keywords: 'settings format match type ends target time sets bowls tournament round robin knockout double elimination draw method rating team assignment rounds sections pools finals greens rinks scoring points bye ladder membership types entry fee rules',
    body: (
      <>
        <p className="help-subhead">Who can enter</p>
        <p>Tick the membership types allowed in. Leave them all unticked to let anyone enter. Members who cannot enter are told why before they try.</p>

        <p className="help-subhead">How it’s played</p>
        <ul>
          <li><K>Format</K>: singles, pairs, triples, fours, 2-4-2 pairs, or custom, and how many <K>bowls per player</K>.</li>
          <li><K>Match type</K>: <K>Set ends</K> (a fixed number of ends), <K>First to</K> (a target score), <K>Timed</K> (minutes — finish the end you are on when time is up), or <K>Sets</K> (a number of sets of so many ends each).</li>
        </ul>

        <p className="help-subhead">The draw</p>
        <ul>
          <li><K>Tournament</K>: <K>Round robin</K> (everyone plays everyone), <K>Knockout</K> (lose once and you are out) or <K>Double elim.</K> (out after two losses).</li>
          <li><K>Draw method</K>: <K>Random</K>, or <K>Rating balanced</K> to spread the strongest players out.</li>
          <li><K>Team assignment</K>: <K>Manual</K> (you or the entrants make the teams), <K>Auto (random)</K> or <K>Auto (rated)</K>, which makes evenly matched teams from ratings. Changing this means re-making the teams.</li>
          <li><K>Rounds</K> (leave blank for a round robin and it works it out) and <K>Sections</K> — pools, if you want the teams split into groups.</li>
          <li><K>Finals series</K>: after a round robin, the top 2, 4 or 8 play off — <K>Final only</K>, <K>Semis + final</K> or <K>Quarters + semis + final</K> — each with its own number of ends.</li>
          <li><K>Greens in use</K> and <K>Rinks in use</K>: which rinks the draw can put games on.</li>
          <li>A date and start time for each round, and for each final. Times start at 9am and space themselves by the game length; change any of them.</li>
        </ul>

        <p className="help-subhead">Scoring</p>
        <ul>
          <li>Points for a <K>Win</K>, <K>Draw</K>, <K>Loss</K>, and a <K>Bye</K> (a round with no opponent).</li>
          <li><K>Method</K>: <K>Standard</K>, or <K>Twilight / social</K>, which also gives points per set won and per end won.</li>
          <li><K>Count towards the club ladder</K>: include its games in players’ ratings and the club ladder. Turn it off for a fun day.</li>
        </ul>

        <p className="help-subhead">Entries and rules</p>
        <p>The entry fee, whether registration is open, the registration link and QR code, and <K>Competition Rules</K> — chosen from your club’s rules bank and shown to members on the competition’s Home tab.</p>
      </>
    ),
  },
  {
    id: 'comp-entries', audience: COMPS, icon: 'qrcode', title: 'Taking entries',
    keywords: 'entries registration link qr code register players add quick add paid email entrants approve close registration',
    body: (
      <>
        <p className="help-subhead">Members enter themselves</p>
        <ul>
          <li>Open the competition’s settings (the gear) for the <K>registration link</K> — <K>Copy</K> it into an email or text — and the <K>QR code</K> to print or put on the club screen.</li>
          <li>Members can also enter from <K>Open for entry</K> on their home page. For team events they name their team and pick their team-mates.</li>
          <li>If an entry needs approving it appears on the bell and under <K>Admin → Registrations</K>.</li>
          <li>When the draw is made, turn <K>Registration is open</K> off in the settings so nobody enters late.</li>
        </ul>
        <p className="help-subhead">Adding players yourself (Players tab)</p>
        <ul>
          <li>Type a name and press Enter to add a new player and enter them in one go. If someone of that name is already at the club you are shown who, so you can pick them instead.</li>
          <li>Choose an existing club player from the list, or <K>add all</K> the club’s players at once for a club day.</li>
          <li>Tick <K>Paid</K> as entry fees come in. The tick sits beside each player on the Teams tab too.</li>
          <li>Select players and use <K>Email Selected Players</K> to send them a quick message or one of your templates — for example the entry confirmation.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'comp-teams', audience: COMPS, icon: 'group', title: 'Making the teams',
    keywords: 'teams auto generate random rated create team import club team add player unassigned rename delete',
    body: (
      <>
        <p>For singles each player is their own team, so there is nothing to do here.</p>
        <ul>
          <li><K>Teams drawn for you</K> (Auto random or rated): press <K>Auto-Generate</K>. Not happy? <K>Re-generate</K>. Anyone left over is listed as <K>Unassigned</K>.</li>
          <li><K>Teams you make</K>: type a <K>New team name</K> and press <K>Create</K>, then use <K>Add player</K> on each team — it shows how many it still needs, such as (1/2).</li>
          <li><K>Add existing team profile from club database</K> brings in one of your club teams with its players.</li>
          <li>Click a team’s name to rename it; the bin deletes it. Teams that members entered themselves arrive already made.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'comp-draw', audience: COMPS, icon: 'random', title: 'Making the draw',
    keywords: 'draw fixtures generate auto manual add match green rink time regenerate delete finals bye knockout double elimination sections finalise lock',
    body: (
      <>
        <p>On the <K>Draw</K> tab press <K>Generate Fixtures</K>. GreenSheet makes every game for the tournament type, puts them on the greens and rinks you chose, and gives each round its start time.</p>
        <ul>
          <li><K>Auto</K> uses the draw method from the settings (random or rated). <K>Manual Draw</K> lets you add each game yourself with <K>Add Match</K>: round, home team, away team, green, rink and time.</li>
          <li><K>Re-generate</K> makes a fresh draw; <K>Delete Draw</K> clears it. <K>Both throw away every game and any scores already entered</K> — Re-generate does not ask first, so only use it before play starts.</li>
          <li><K>Knockout</K>: an uneven number of teams gets byes, and a team with a bye <K>advances automatically</K>. Later rounds fill in as results come in.</li>
          <li><K>Double elimination</K>: the losers’ bracket sits below a line on the draw.</li>
          <li><K>Finals</K>: once the round robin is played, press <K>Generate Finals</K> to draw the top teams into the play-offs.</li>
          <li>When you are happy, tick <K>Draw finalised</K>. That locks the draw against accidental changes; untick it to edit again.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'comp-scoring', audience: COMPS, icon: 'pencil-square-o', title: 'Entering scores',
    keywords: 'scores score sheet ends tab enter add end sets tie breaker extra end forfeit not played save approve awaiting approval players submit',
    body: (
      <>
        <p>On the <K>Scores</K> tab, tap a game (<K>Tap to score</K>) to open its score sheet.</p>
        <ul>
          <li>Type each end’s score. Press Tab or Enter to move down the column. Only one side scores on an end, so the other side’s box is skipped.</li>
          <li><K>Timed</K> and <K>first to</K> games can run long: <K>Add End</K> adds another row.</li>
          <li><K>Sets</K> games show a table per set, with the sets and shots totalled at the top. In twilight scoring, if the sets are tied a <K>Tie-Breaker Extra End</K> decides it.</li>
          <li><K>Not Played</K> records a game that did not happen. <K>[Team] forfeit</K> gives the game to the other side.</li>
          <li>Press <K>Save Scores</K>. The standings update straight away.</li>
        </ul>
        <p className="help-subhead">Scores from players</p>
        <p>Players can put in the score for their own game. It shows as <K>Awaiting Approval</K> until you approve it — from the Scores tab, or through the bell. A knockout game shows <K>Waiting for previous round</K> until both teams are known.</p>
      </>
    ),
  },
  {
    id: 'comp-standings', audience: COMPS, icon: 'list-ol', title: 'Points, standings and twilight scoring',
    keywords: 'points standings ranking ties tiebreak shot difference ends won head to head twilight bonus bye ladder results analytics',
    body: (
      <>
        <p>The <K>Results</K> tab shows <K>Results &amp; Standings</K>. Teams are ranked on points, then on:</p>
        <ol>
          <li>shot difference (shots for minus shots against),</li>
          <li>ends won,</li>
          <li>shots for,</li>
          <li>and then head to head.</li>
        </ol>
        <p>The order is written under the table. Click a column heading to sort by it for a look; <K>Reset to default ranking order</K> puts it back.</p>
        <ul>
          <li>Each game gives the win, draw or loss points from the settings. A bye gives the bye points.</li>
          <li>In <K>twilight scoring</K> a team also gets the bonus for every set and every end it wins, so a close loss still earns something.</li>
          <li><K>Analytics</K> on the Results tab has charts of how the competition went.</li>
          <li>If the competition counts towards the club ladder, its games feed every player’s rating.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'comp-finish', audience: COMPS, icon: 'flag-checkered', title: 'Finishing, and fixing mistakes',
    keywords: 'finalise competition complete winner reopen score fixes locked lock draw finalised mistake correct',
    body: (
      <>
        <p>When every game is scored, press <K>Finalise Competition</K> on the Results tab. If any are still to play it tells you how many. Finalising:</p>
        <ul>
          <li>marks it completed, so it moves to past competitions and its winner shows under <K>Recent Winners</K>;</li>
          <li>locks the players, teams, draw and scores.</li>
        </ul>
        <p>Found a mistake afterwards? <K>Reopen for Score Fixes</K> unlocks the scores only — the players, teams and draw stay locked so the results cannot shift under you. Correct the score and finalise again.</p>
        <p>Before finalising, the <K>Draw finalised</K> tick on the Draw tab is the lighter lock: it stops the draw changing but leaves everything else open.</p>
      </>
    ),
  },

  /* ------------------------------------------------------------ site admins */
  {
    id: 'site-admin', audience: SITE, icon: 'sitemap', title: 'Clubs, plans and super-admins',
    keywords: 'super admin site clubs plans defaults club selector all clubs download export zip backup',
    body: (
      <ul>
        <li><K>Site</K> (More menu): clubs, plans, and the defaults new clubs start with.</li>
        <li><K>Download everything</K> under a club gives you all of that club’s information as a zip — members (also as a spreadsheet), competitions and results, events, newsletters, the mailing list and every picture. Use it when a club leaves (the terms promise a copy within 30 days) or asks for its records. It holds every member’s personal details, so keep it secure.</li>
        <li>The club selector in the footer switches between clubs, or shows all of them. With <K>All clubs</K> the season filter uses calendar years.</li>
        <li>Only a super-admin can make another super-admin, move members or competitions between clubs, or change a super-admin’s account. The last super-admin cannot be demoted.</li>
      </ul>
    ),
  },
];

const GROUPS = [
  { audience: PLAYER, label: 'Using GreenSheet' },
  { audience: ADMIN, label: 'Running the club' },
  { audience: COMPS, label: 'Running competitions' },
  { audience: SITE, label: 'Running the site' },
];

/* Search reads the words of a topic as well as its title and keywords.
   The body is JSX, so its text is gathered by walking the tree. */
function textOf(node) {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join(' ');
  if (node.props) return textOf(node.props.children);
  return '';
}
const HAYSTACK = Object.fromEntries(TOPICS.map(t => [t.id,
  `${t.title} ${t.keywords} ${textOf(t.body)}`.toLowerCase()]));

function HelpView({ isAdmin, isSuperAdmin }) {
  const [query, setQuery] = React.useState('');
  const [asMember, setAsMember] = React.useState(false);

  const allowed = new Set([PLAYER]);
  if (isAdmin && !asMember) { allowed.add(ADMIN); allowed.add(COMPS); }
  if (isSuperAdmin && !asMember) allowed.add(SITE);

  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = TOPICS.filter(t => allowed.has(t.audience)
    && words.every(w => HAYSTACK[t.id].includes(w)));
  const searching = words.length > 0;

  return (
    <div className="content-medium help-page">
      <h2 className="page-title">Help</h2>
      <p className="help-intro">
        {isAdmin && !asMember
          ? 'Everything GreenSheet does — for you as a player, and for running the club.'
          : 'How to use GreenSheet at your club.'}
      </p>

      <div className="help-tools">
        <input className="w3-input help-search" type="search" value={query}
          aria-label="Search help" placeholder="Search help — e.g. password, posters, renew"
          onChange={e => setQuery(e.target.value)} />
        {isAdmin && (
          <Segmented ariaLabel="Whose help to show"
            options={[{ value: 'all', label: 'Everything' }, { value: 'member', label: 'As a member sees it' }]}
            value={asMember ? 'member' : 'all'} onChange={v => setAsMember(v === 'member')} />
        )}
      </div>

      {shown.length === 0 && (
        <p className="help-empty">
          Nothing matches “{query.trim()}”.{' '}
          <button type="button" className="auth-forgot" onClick={() => setQuery('')}>Show all topics</button>
        </p>
      )}

      {/* Sections are siblings so each draws its own rule; see the note on
          .gs-section in ui.css. */}
      {GROUPS.filter(g => allowed.has(g.audience)).map(group => {
        const topics = shown.filter(t => t.audience === group.audience);
        if (!topics.length) return null;
        return (
          <Section key={group.audience} label={group.label}>
            {topics.map(t => (
              /* Open while searching, so a match is read rather than hunted
                 for; closed otherwise, so the page is a list of questions. */
              <details key={`${t.id}-${searching}`} className="help-topic" open={searching}>
                <summary>
                  <i className={`fa fa-${t.icon} help-topic-icon`} aria-hidden="true"></i>
                  {t.title}
                </summary>
                <div className="help-topic-body">{t.body}</div>
              </details>
            ))}
          </Section>
        );
      })}

      <p className="help-footer">
        Still stuck? Ask your club admin{isAdmin ? ', or whoever runs GreenSheet for your club' : ''}.
        {' '}<a href="/privacy.html" target="_blank" rel="noopener">Privacy Policy</a> · <a href="/terms.html" target="_blank" rel="noopener">Terms of Use</a>
      </p>
    </div>
  );
}

export { TOPICS };
export default HelpView;
